/**
 * The location hierarchy, top down. Mirrors bovid-api's `LocationLevel`: every level is served
 * by `/locations/:level` and sits under the one before it.
 */
export const LOCATION_LEVELS = [
  { key: 'states', noun: 'State', filter: 'stateId', codeLabel: 'LGD Code' },
  { key: 'districts', noun: 'District', filter: 'districtId', codeLabel: 'LGD Code' },
  { key: 'mandals', noun: 'Mandal', filter: 'mandalId', codeLabel: 'LGD Code' },
  { key: 'villages', noun: 'Village', filter: 'villageId', codeLabel: 'LGD Code' },
  { key: 'hamlets', noun: 'Hamlet', filter: null, codeLabel: 'Code' },
] as const;

export type LocationLevel = (typeof LOCATION_LEVELS)[number];
export type LocationLevelKey = LocationLevel['key'];
/** The list's filter for each level a record can sit under. */
export type AncestorFilter = NonNullable<LocationLevel['filter']>;

export const isLocationLevel = (key: string): key is LocationLevelKey =>
  LOCATION_LEVELS.some((level) => level.key === key);

export interface LevelPlan {
  level: LocationLevel;
  /** The levels above, from the state down. */
  ancestors: readonly (LocationLevel & { filter: AncestorFilter })[];
  /** The level below, if any. */
  child: LocationLevel | undefined;
}

const LEVEL_PLANS = Object.fromEntries(
  LOCATION_LEVELS.map((level, depth) => [
    level.key,
    {
      level,
      ancestors: LOCATION_LEVELS.slice(0, depth) as LevelPlan['ancestors'],
      child: LOCATION_LEVELS[depth + 1],
    },
  ]),
) as Record<LocationLevelKey, LevelPlan>;

export const levelPlan = (key: LocationLevelKey): LevelPlan => LEVEL_PLANS[key];
